import Cookies from 'js-cookie';
import { COMPANY_WIDE_BRANCH } from '@rapid-recovery-agency-inc/insightt-shared';

import storage from '../../helpers/storage';
import AuthActions from '../../redux/actions/auth.actions';
import AppActions from '../../redux/actions/app.actions';
import ReportsActions from '../../redux/actions/reports.actions';
import GoalsActions from '../../redux/actions/goals.actions';
import TodoListActions from '../../redux/actions/todolist.actions';
import TaskManagementActions from '../../redux/actions/taskmanagement.actions';
import HumanResourcesActions from '../../redux/actions/humanresources.actions';
import ChecklistActions from '../../redux/actions/checklist.actions';
import ShiftsActions from '../../redux/actions/shifts.actions';
import ShiftDetailsActions from '../../redux/actions/shift-details.actions';
import ShiftActions from '../../redux/actions/shift.actions';
import StatsActions from '../../redux/actions/stats.actions';
import { store } from '../../redux/store';
import { filters } from '../../shared/filters/filters';
import { client } from '../../shared/apollo';
import { InMemoryCache } from '../../shared/cache';
import { deleteAllCache } from '../../cache/cache.utils';
import { onBranchEvent } from '../../shared/events';
import { getAppAuthBoundary, type CleanupOptions } from '../auth';
import { resetAnalyticsIdentity } from '../../shared/analytics/services';

/**
 * T037 (FR-021, FR-022; spec.md US5) — the ONE consolidated session-cleanup routine every
 * session-invalidation trigger point in this app-level (non-auth-secret) layer delegates to:
 * the reactive "session died server-side" path below (`cleanSessionData`, called from
 * `system/redux/actions.ts`'s `fetchSession` thunk) and the explicit user-initiated logout saga
 * (`redux/sagas/auth.saga.jsx#logoutRequest`).
 *
 * Previously (pre-T037) these two call sites each duplicated their own partial subset of this
 * work — `cleanSessionData` cleared only storage/cookies/filters/Apollo cache and dispatched the
 * legacy `AuthActions.clearRequest()` (which only resets `status`, NOT `user`/`token`/
 * `deviceToken`); `logoutRequest` separately cleared the same storage/cookies/filters plus its own
 * additional Intercom/legacy-cache/domain-Redux-slice/Apollo-reset steps, inline. Neither cleared
 * third-party analytics identity (Mixpanel/Clevertap) at all. This routine merges the FULL set of
 * both so a second user on the same browser never observes the previous user's cached data,
 * company/branch selection, analytics identity, or notification identity (spec.md US5 Acceptance
 * Scenario 4), regardless of which of the two triggers fired.
 *
 * Mode-neutral (FR-008): `getAppAuthBoundary().cleanup()` clears the SSO in-memory credential
 * envelope AND the legacy adapter's cookie storage, then — only when an SSO session existed —
 * attempts the one discovered client-scoped `end_session` termination (T036), never restoring the
 * local state already cleared above regardless of that attempt's outcome (FR-022). This is called
 * LAST, after every local, synchronous piece of clearing below has already happened, matching the
 * "local-first" ordering the boundary's own `cleanup()` uses internally.
 *
 * Single-flight (spec.md US5 Acceptance Scenario 2 "one cleanup ... occurs" for concurrent
 * unauthorized events; FR-021 "exactly once"): several callers (a `fetchSession` race, a
 * concurrently-dispatched `LOGOUT_REQUEST`) could invoke this within the same tick. Collapsing
 * concurrent calls into the same in-flight promise avoids repeating the domain-Redux clears, the
 * Apollo/legacy cache reset, the Intercom shutdown/reboot, or the analytics identity reset more
 * than once — mirroring `auth-boundary.ts`'s own `SingleFlightEpisode` pattern at this layer,
 * which this module cannot reuse directly (it is boundary-internal, T017's declared scope) but can
 * imitate with the same "synchronously store the in-flight promise before returning" shape.
 */
let cleanupInFlight: Promise<void> | null = null;

const runSessionCleanup = async (options: CleanupOptions): Promise<void> => {
  // Local credential/session storage and cookies (unchanged from the pre-T037 behavior).
  storage.remove('user');
  storage.remove('timezone');
  storage.remove('subscription');
  storage.remove('device_token');
  storage.remove('isNotificationBlocked');
  storage.set('session_timeout', true);
  Cookies.remove('user_type');
  Cookies.remove('access_token');

  // Session-tied filters/preferences (FR-021).
  Object.values(filters).forEach((filter) => {
    filter.clearPersistedValue();
  });

  // Company/branch selection (FR-021; previously only reset by the explicit-logout saga) — reset
  // to the company-wide baseline so no prior branch selection remains visible.
  onBranchEvent.dispatch(COMPANY_WIDE_BRANCH);

  // Client state: every domain Redux slice previously only cleared by the explicit-logout saga's
  // local `clearRequest()` generator (see `redux/sagas/auth.saga.jsx`), now shared by both trigger
  // points. `AuthActions.clearAuthRequest()` (not the weaker `clearRequest()` the pre-T037
  // `cleanSessionData` dispatched) is used deliberately — it is a strict superset, additionally
  // clearing `user`/`token`/`deviceToken` from the legacy auth slice, which the reactive
  // "session died server-side" path was never actually doing before this task.
  InMemoryCache.clear();
  store.dispatch(AuthActions.clearAuthRequest());
  store.dispatch(AppActions.clearAppRequest());
  store.dispatch(ReportsActions.clearReportsRequest());
  store.dispatch(GoalsActions.clearGoalsRequest());
  store.dispatch(TodoListActions.clearTodoListRequest());
  store.dispatch(TaskManagementActions.clearTasksRequest());
  store.dispatch(HumanResourcesActions.clearHumanResourcesRequest());
  store.dispatch(ChecklistActions.clearChecklistRequest());
  store.dispatch(ShiftsActions.clearShiftsRequest());
  store.dispatch(ShiftDetailsActions.clearShiftDetailsRequest());
  store.dispatch(ShiftActions.clearShiftRequest());
  store.dispatch(StatsActions.clearStatsRequest());

  // Legacy compatibility cache (deprecated `sessionStorage`-backed cache; FR-021 "cached server
  // data").
  deleteAllCache();

  // Third-party user-identification integrations (FR-021; spec.md US5 Acceptance Scenario 4
  // "no ... analytics identity, or notification identity from the prior user is observable").
  // Intercom shutdown+reboot (unchanged from the pre-T037 saga behavior) drops the previous user's
  // notification identity; `resetAnalyticsIdentity()` (new — T037) drops the Mixpanel identity a
  // later `identify()` call would otherwise still be scoped under.
  // `window.Intercom` has no ambient type declaration in this codebase (the real snippet is
  // injected by the production HTML shell) — cast narrowly rather than widening to `any`.
  const intercom = window as unknown as { Intercom: (...args: unknown[]) => void };
  intercom.Intercom('shutdown');
  intercom.Intercom('boot', { app_id: 'gaz7e1i2' });
  resetAnalyticsIdentity();

  // Cached server data (FR-021).
  await client.cache.reset();

  // Mode-neutral credential/session termination (T036; FR-021, FR-022, FR-032): clears the SSO
  // in-memory envelope and legacy adapter storage, then — only when an SSO session existed —
  // attempts the ONE discovered client-scoped `end_session` provider termination, bounded by an
  // explicit timeout. Every local clearing step above already happened and is never rolled back
  // regardless of whether this settles COMPLETE or PARTIAL_LOGOUT.
  await getAppAuthBoundary().cleanup(options);
};

// Deliberately NOT declared `async` — an `async function` always wraps its return value in a
// brand-new Promise, which would break the single-flight identity guarantee (`cleanup.test.ts`'s
// own `expect(firstPromise).toBe(secondPromise)` pattern, mirrored by this task's
// `cleanup-integrations.test.ts`): two calls while a transaction is in flight must return the
// EXACT SAME promise object, not two different promises that merely resolve at the same time.
export const cleanSessionData = (options: CleanupOptions = {}): Promise<void> => {
  if (!cleanupInFlight) {
    cleanupInFlight = runSessionCleanup(options).finally(() => {
      cleanupInFlight = null;
    });
  }
  return cleanupInFlight;
};
